import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PublicFooter from "@/components/PublicFooter";

const PLATFORM_CONTACT = process.env.REACT_APP_PLATFORM_CONTACT_EMAIL || "info@jobsbook.app";
const DEMO_URL = process.env.REACT_APP_PLATFORM_DEMO_URL || "";

// Phase P.26 — country picker options (ISO 3166-1 alpha-2). Trimmed to the
// EU + UK + a handful of common non-EU markets. Stripe accepts any ISO code
// so we can extend this list without a backend change if a new tenant asks.
const SIGNUP_COUNTRIES = [
  { code: "IE", name: "Ireland" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "AT", name: "Austria" },
  { code: "BE", name: "Belgium" },
  { code: "BG", name: "Bulgaria" },
  { code: "HR", name: "Croatia" },
  { code: "CY", name: "Cyprus" },
  { code: "CZ", name: "Czechia" },
  { code: "DK", name: "Denmark" },
  { code: "EE", name: "Estonia" },
  { code: "FI", name: "Finland" },
  { code: "FR", name: "France" },
  { code: "DE", name: "Germany" },
  { code: "GR", name: "Greece" },
  { code: "HU", name: "Hungary" },
  { code: "IT", name: "Italy" },
  { code: "LV", name: "Latvia" },
  { code: "LT", name: "Lithuania" },
  { code: "LU", name: "Luxembourg" },
  { code: "MT", name: "Malta" },
  { code: "NL", name: "Netherlands" },
  { code: "PL", name: "Poland" },
  { code: "PT", name: "Portugal" },
  { code: "RO", name: "Romania" },
  { code: "SK", name: "Slovakia" },
  { code: "SI", name: "Slovenia" },
  { code: "ES", name: "Spain" },
  { code: "SE", name: "Sweden" },
];

export default function Signup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "", password: "", confirm: "", company_name: "", manager_name: "",
    // Phase P.26 — mandatory billing address (Country defaults IE; user can
    // change via the dropdown). VAT number is optional and kept in our DB only.
    country: "IE", address_line1: "", city: "", postal_code: "", vat_number: "",
  });
  // Phase P.18 — GDPR consent state. Terms + Privacy are REQUIRED; marketing
  // is OPTIONAL and defaults OFF (opt-in per Art. 6(1)(a) GDPR).
  const [consent, setConsent] = useState({ terms: false, privacy: false, marketing: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(null); // { email, dev_token? }

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onConsent = (k) => (v) => setConsent((c) => ({ ...c, [k]: !!v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) { setError("Passwords don't match."); return; }
    if (form.password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (!consent.terms || !consent.privacy) {
      setError("Please agree to the Terms of Service and the Privacy Policy to continue.");
      return;
    }
    // Phase P.26 — hard-gate the mandatory address on the client so the
    // user gets an immediate error rather than a slow round-trip 400.
    if (!form.address_line1.trim() || !form.city.trim() || !form.postal_code.trim()) {
      setError("Please complete your business address (line 1, city, postal code).");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/signup/start", {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        company_name: form.company_name.trim(),
        manager_name: form.manager_name.trim() || "Manager",
        country: form.country.trim().toUpperCase(),
        address_line1: form.address_line1.trim(),
        city: form.city.trim(),
        postal_code: form.postal_code.trim(),
        vat_number: form.vat_number.trim(),
        consent: {
          terms: consent.terms,
          privacy: consent.privacy,
          marketing: consent.marketing,
          version: "2026-07-27",
        },
      });
      setSent(data);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-primary text-primary-foreground grid place-items-center p-6" data-testid="signup-sent-screen">
        <div className="max-w-md w-full bg-card text-card-foreground rounded-[10px] p-8 border border-border">
          <div className="flex items-center gap-3 text-accent">
            <CheckCircle2 className="h-8 w-8" />
            <span className="mono text-[11px] uppercase tracking-[0.25em]">// Check your inbox</span>
          </div>
          <h1 className="mt-3 text-2xl font-black tracking-tight">We sent you a verification link</h1>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Click the link in the email we just sent to <span className="font-mono font-semibold">{sent.email}</span> to finish creating your account. The link expires in 30 minutes.
          </p>
          {sent.dev_token && (
            <div className="mt-4 p-3 border border-amber-500 bg-amber-50 text-amber-900 rounded-md text-xs">
              <div className="font-bold mb-1">Dev mode: email delivery not configured.</div>
              <Link to={`/signup/verify?token=${sent.dev_token}`} className="text-accent underline font-semibold break-all" data-testid="signup-dev-verify-link">
                Click here to verify
              </Link>
            </div>
          )}
          <div className="mt-6 flex items-center gap-3">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const { data } = await api.post("/signup/resend-verification", { email: sent.email });
                  setSent(data);
                } catch (err) {
                  setError(formatApiError(err?.response?.data?.detail));
                }
              }}
              data-testid="signup-resend-btn"
            >
              Resend email
            </Button>
            <Link to="/login" className="text-sm text-muted-foreground hover:text-accent underline-offset-2 hover:underline" data-testid="signup-sent-back-login">
              Back to sign in
            </Link>
          </div>
          {error && <div className="mt-3 text-xs text-destructive" data-testid="signup-sent-error">{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary text-primary-foreground grid lg:grid-cols-2" data-testid="signup-page">
      {/* Left marketing panel */}
      <div className="hidden lg:flex flex-col justify-between p-10">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="signup-brand-home">
          <div className="h-8 w-8 rounded-md bg-accent grid place-items-center text-accent-foreground font-black">J</div>
          <span className="font-extrabold tracking-tight text-lg">JobsBook</span>
        </Link>
        <div>
          <div className="mono text-[11px] uppercase tracking-[0.3em] text-accent">// Start for free</div>
          <h1 className="mt-3 text-4xl font-black tracking-tight leading-[1.1]">
            7 days of <span className="text-accent">full access.</span><br />No card required.
          </h1>
          <p className="mt-4 text-primary-foreground/70 text-base leading-relaxed max-w-md">
            Plan jobs, dispatch staff, and invoice customers. Your trial unlocks everything we have — calendar, staff portal, ledger, full invoicing, reports. Pick the plan that fits after day 7.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-primary-foreground/85">
            {["Basic €29/month — bookings + customers + staff + ledger",
              "Pro €40/month — full invoicing + reports + receipts",
              "Cancel anytime from Stripe Customer Portal",
              "Data retained 30 days after trial ends"].map((f) => (
              <li key={f} className="flex gap-2 items-start"><CheckCircle2 className="h-4 w-4 text-accent shrink-0 mt-0.5" />{f}</li>
            ))}
          </ul>
        </div>
        <div className="mono text-[11px] uppercase tracking-[0.25em] text-primary-foreground/40">
          {DEMO_URL ? (
            <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="hover:text-accent" data-testid="signup-demo-link">
              Prefer a quick demo? Book 10 min with us →
            </a>
          ) : (
            <>Need help? <a className="hover:text-accent" href={`mailto:${PLATFORM_CONTACT}`}>{PLATFORM_CONTACT}</a></>
          )}
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 md:p-10 bg-background text-foreground">
        <div className="w-full max-w-md">
          <div className="mono text-[11px] uppercase tracking-[0.3em] text-accent">// Create your tenant</div>
          <h2 className="mt-2 text-3xl font-black tracking-tight">Start your free trial</h2>
          <p className="text-sm text-muted-foreground mt-1">Already have an account? <Link to="/login" className="text-accent font-semibold hover:underline" data-testid="signup-signin-link">Sign in</Link>.</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="signup-form">
            <div>
              <Label htmlFor="company_name">Company name</Label>
              <Input id="company_name" required value={form.company_name} onChange={on("company_name")} placeholder="Acme Pumping Ltd" data-testid="signup-company-input" />
            </div>
            <div>
              <Label htmlFor="manager_name">Your name</Label>
              <Input id="manager_name" value={form.manager_name} onChange={on("manager_name")} placeholder="Jane Doyle" data-testid="signup-name-input" />
            </div>
            <div>
              <Label htmlFor="email">Work email</Label>
              <Input id="email" type="email" required value={form.email} onChange={on("email")} placeholder="you@company.com" data-testid="signup-email-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" required minLength={8} value={form.password} onChange={on("password")} data-testid="signup-password-input" />
              </div>
              <div>
                <Label htmlFor="confirm">Confirm</Label>
                <Input id="confirm" type="password" required minLength={8} value={form.confirm} onChange={on("confirm")} data-testid="signup-confirm-input" />
              </div>
            </div>
            {/* Phase P.26 — mandatory business address. Country dropdown
                defaults to Ireland (the historical customer base) but is
                editable so EU tenants get charged the correct VAT rate at
                Stripe Checkout. VAT number is optional and stays in our DB
                (never forwarded to Stripe — see Phase P.26 Task 2). */}
            <div className="border-t border-border pt-4 space-y-3" data-testid="signup-address-block">
              <div>
                <h3 className="text-sm font-extrabold uppercase tracking-wider text-muted-foreground">Business address</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Required for VAT compliance on your subscription invoices.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="country">Country</Label>
                  <select
                    id="country"
                    required
                    value={form.country}
                    onChange={on("country")}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    data-testid="signup-country-select"
                  >
                    {SIGNUP_COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="postal_code">Postal code</Label>
                  <Input id="postal_code" required value={form.postal_code} onChange={on("postal_code")} placeholder="D02 XY45" data-testid="signup-postal-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="address_line1">Address line 1</Label>
                <Input id="address_line1" required value={form.address_line1} onChange={on("address_line1")} placeholder="24 Main Street" data-testid="signup-address1-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input id="city" required value={form.city} onChange={on("city")} placeholder="Dublin" data-testid="signup-city-input" />
                </div>
                <div>
                  <Label htmlFor="vat_number">VAT number <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Input id="vat_number" value={form.vat_number} onChange={on("vat_number")} placeholder="IE1234567X" data-testid="signup-vat-input" />
                </div>
              </div>
            </div>
            {error && <div className="text-sm text-destructive" data-testid="signup-error">{error}</div>}
            {/* Phase P.18 — GDPR consent capture. Terms + Privacy are hard
                gates (Art. 6(1)(b)); Marketing is opt-in (Art. 6(1)(a)).
                Wrapper testid `signup-terms-acknowledgement` preserved from
                Phase 19 so the JS-bundle regression test still resolves it. */}
            <div
              className="space-y-2 border-t border-border pt-4"
              data-testid="signup-consent-block"
              id="signup-terms-acknowledgement"
              data-legacy-testid="signup-terms-acknowledgement"
            >
              <label className="flex items-start gap-2 text-xs text-foreground/85 leading-relaxed cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-accent"
                  checked={consent.terms}
                  onChange={(e) => onConsent("terms")(e.target.checked)}
                  data-testid="signup-consent-terms"
                  required
                />
                <span>
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-accent underline-offset-2 hover:underline">
                    Terms of Service
                  </Link>.
                </span>
              </label>
              <label className="flex items-start gap-2 text-xs text-foreground/85 leading-relaxed cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-accent"
                  checked={consent.privacy}
                  onChange={(e) => onConsent("privacy")(e.target.checked)}
                  data-testid="signup-consent-privacy"
                  required
                />
                <span>
                  I have read the{" "}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-accent underline-offset-2 hover:underline">
                    Privacy Policy
                  </Link>{" "}
                  and understand how my data is processed.
                </span>
              </label>
              <label className="flex items-start gap-2 text-xs text-foreground/70 leading-relaxed cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-accent"
                  checked={consent.marketing}
                  onChange={(e) => onConsent("marketing")(e.target.checked)}
                  data-testid="signup-consent-marketing"
                />
                <span>
                  <em>Optional:</em> send me product updates and tips. You can unsubscribe at any time.
                </span>
              </label>
            </div>
            <Button type="submit" disabled={submitting} className="w-full btn-cta" data-testid="signup-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
              Create account & send verification
            </Button>
            <p className="text-[11px] text-muted-foreground mono uppercase tracking-[0.18em]">By signing up you agree to receive a verification email.</p>
          </form>
          <div className="mt-6 pt-5 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
            <Link to="/" className="hover:text-accent mono uppercase tracking-wider" data-testid="signup-back-home">← Back home</Link>
            {DEMO_URL && (
              <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="mono uppercase tracking-wider hover:text-accent lg:hidden" data-testid="signup-demo-link-mobile">
                Book a demo →
              </a>
            )}
          </div>
          <div className="mt-5">
            <PublicFooter tone="dark" />
          </div>
        </div>
      </div>
    </div>
  );
}
